import React, { useState, useEffect } from 'react';
import '../../styles/AdminPages.css';
import './GestionOpportunites.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';
import projetService from '../../services/projetService';

function GestionProjets() {
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    titre: '',
    type: 'projet',
    description: '',
    contenu: '',
    image: null,
    objectif_montant: '',
    montant_collecte: 0,
    date_debut: '',
    date_fin: '',
    statut: 'en_cours',
    beneficiaires: [''],
    partenaires: [''],
    publie: true
  });

  useEffect(() => {
    loadProjets();
  }, [filterType]);

  const loadProjets = async () => {
    try {
      setLoading(true);
      const params = filterType !== 'all' ? { type: filterType } : {};
      try {
        const response = await projetService.admin.getAll(params);
        setProjets(response.data.data || response.data || []);
      } catch (adminError) {
        console.warn('Fallback vers les projets publics:', adminError);
        const response = await projetService.getAll(params);
        setProjets(response.data.data || response.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des projets:', error);
      setProjets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Ajouter tous les champs
      Object.keys(formData).forEach(key => {
        if (key === 'image') {
          // Ajouter l'image SEULEMENT si elle existe et est un fichier
          if (formData[key] && formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          }
        } else if (key === 'beneficiaires') {
          const beneficiaires = formData.beneficiaires.filter(b => b.trim() !== '');
          formDataToSend.append(key, JSON.stringify(beneficiaires));
        } else if (key === 'partenaires') {
          const partenaires = formData.partenaires.filter(p => p.trim() !== '');
          formDataToSend.append(key, JSON.stringify(partenaires));
        } else if (key === 'publie') {
          // Convertir le booléen en string
          formDataToSend.append(key, formData[key] ? '1' : '0');
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (editingId) {
        // Ajouter _method pour Laravel
        formDataToSend.append('_method', 'PUT');
        await projetService.admin.update(editingId, formDataToSend);
        alert('Projet mis à jour avec succès');
      } else {
        await projetService.admin.create(formDataToSend);
        alert('Projet créé avec succès');
      }
      
      setShowModal(false);
      resetForm();
      loadProjets();
    } catch (error) {
      console.error('Erreur complète:', error);
      console.error('Réponse:', error.response?.data);
      alert(`Erreur lors de l'enregistrement: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await projetService.admin.getById(id);
      const projet = response.data;
      setFormData({
        titre: projet.titre,
        type: projet.type,
        description: projet.description,
        contenu: projet.contenu,
        image: null,
        objectif_montant: projet.objectif_montant || '',
        montant_collecte: projet.montant_collecte || 0,
        date_debut: projet.date_debut || '',
        date_fin: projet.date_fin || '',
        statut: projet.statut,
        beneficiaires: projet.beneficiaires || [''],
        partenaires: projet.partenaires || [''],
        publie: projet.publie
      });
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) return;
    try {
      await projetService.admin.delete(id);
      alert('Projet supprimé avec succès');
      loadProjets();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      type: 'projet',
      description: '',
      contenu: '',
      image: null,
      objectif_montant: '',
      montant_collecte: 0,
      date_debut: '',
      date_fin: '',
      statut: 'en_cours',
      beneficiaires: [''],
      partenaires: [''],
      publie: true
    });
    setEditingId(null);
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Projets & Cagnottes" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="page-header">
            <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
              + Nouveau Projet
            </button>
          </div>

          <div className="filters">
            <button className={`filter-btn ${filterType === 'all' ? 'active' : ''}`} onClick={() => setFilterType('all')}>Tous</button>
            <button className={`filter-btn ${filterType === 'projet' ? 'active' : ''}`} onClick={() => setFilterType('projet')}>Projets</button>
            <button className={`filter-btn ${filterType === 'cagnotte' ? 'active' : ''}`} onClick={() => setFilterType('cagnotte')}>Cagnottes</button>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <div className="opportunites-table">
              <table>
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Type</th>
                    <th>Objectif</th>
                    <th>Collecté</th>
                    <th>Progression</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projets.map((projet) => {
                    const pourcentage = projet.objectif_montant > 0 
                      ? Math.round((projet.montant_collecte / projet.objectif_montant) * 100) 
                      : 0;
                    return (
                      <tr key={projet.id}>
                        <td>{projet.titre}</td>
                        <td>
                          <span className={`badge badge-${projet.type}`}>
                            {projet.type === 'projet' ? 'Projet' : 'Cagnotte'}
                          </span>
                        </td>
                        <td>{projet.objectif_montant ? `${projet.objectif_montant.toLocaleString()} FCFA` : '-'}</td>
                        <td>{projet.montant_collecte.toLocaleString()} FCFA</td>
                        <td>{pourcentage}%</td>
                        <td>
                          <span className={`badge ${projet.statut === 'en_cours' ? 'badge-success' : 'badge-draft'}`}>
                            {projet.statut === 'en_cours' ? 'En cours' : projet.statut === 'termine' ? 'Terminé' : 'Annulé'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-edit" onClick={() => handleEdit(projet.id)}>Modifier</button>
                            <button className="btn-delete" onClick={() => handleDelete(projet.id)}>Supprimer</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingId ? 'Modifier le projet' : 'Nouveau projet'}</h2>
                  <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                </div>
                <form onSubmit={handleSubmit} className="opportunite-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Titre *</label>
                      <input type="text" value={formData.titre} onChange={(e) => setFormData({ ...formData, titre: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label>Type *</label>
                      <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} required>
                        <option value="projet">Projet</option>
                        <option value="cagnotte">Cagnotte</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description courte *</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" required />
                  </div>

                  <div className="form-group">
                    <label>Contenu détaillé *</label>
                    <textarea value={formData.contenu} onChange={(e) => setFormData({ ...formData, contenu: e.target.value })} rows="5" required />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Objectif montant (FCFA)</label>
                      <input type="number" min="0" value={formData.objectif_montant} onChange={(e) => setFormData({ ...formData, objectif_montant: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Montant collecté (FCFA)</label>
                      <input type="number" min="0" value={formData.montant_collecte} onChange={(e) => setFormData({ ...formData, montant_collecte: e.target.value })} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Date début</label>
                      <input type="date" value={formData.date_debut} onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Date fin</label>
                      <input type="date" value={formData.date_fin} onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Statut</label>
                      <select value={formData.statut} onChange={(e) => setFormData({ ...formData, statut: e.target.value })}>
                        <option value="en_cours">En cours</option>
                        <option value="termine">Terminé</option>
                        <option value="annule">Annulé</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Image</label>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })} 
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Bénéficiaires</label>
                    {formData.beneficiaires.map((beneficiaire, index) => (
                      <div key={index} className="array-field">
                        <input type="text" value={beneficiaire} onChange={(e) => handleArrayFieldChange('beneficiaires', index, e.target.value)} placeholder="Bénéficiaire..." />
                        {formData.beneficiaires.length > 1 && <button type="button" onClick={() => removeArrayField('beneficiaires', index)}>×</button>}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('beneficiaires')}>+ Ajouter un bénéficiaire</button>
                  </div>

                  <div className="form-group">
                    <label>Partenaires</label>
                    {formData.partenaires.map((partenaire, index) => (
                      <div key={index} className="array-field">
                        <input type="text" value={partenaire} onChange={(e) => handleArrayFieldChange('partenaires', index, e.target.value)} placeholder="Partenaire..." />
                        {formData.partenaires.length > 1 && <button type="button" onClick={() => removeArrayField('partenaires', index)}>×</button>}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('partenaires')}>+ Ajouter un partenaire</button>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={formData.publie} onChange={(e) => setFormData({ ...formData, publie: e.target.checked })} />
                      Publier immédiatement
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Annuler</button>
                    <button type="submit" className="btn-submit">{editingId ? 'Mettre à jour' : 'Créer'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default GestionProjets;